package com.castly.admin.api

import com.castly.shared.dto.AdminSectionDto
import com.castly.shared.dto.CreateSectionRequest
import com.castly.shared.dto.ReorderSectionsRequest
import com.castly.shared.dto.UpdateSectionRequest
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * API client for home section management operations
 *
 * Features:
 * - Create, update, delete, and restore sections
 * - Batch reorder sections
 * - List sections (active, archived, or all)
 * - Handles authentication with JWT tokens
 * - Handles 401 session expiration
 */
class HomeSectionApiClient(val baseUrl: String) {

    @Serializable
    data class SectionsResponse(val sections: List<AdminSectionDto>)

    @Serializable
    data class MessageResponse(val message: String)

    @Serializable
    data class ErrorResponse(val error: String)

    @Serializable
    data class SearchItemsRequest(val query: String, val contentType: String, val limit: Int? = 10)

    @Serializable
    data class AddItemRequest(val itemId: String)

    @Serializable
    data class AddItemsByTagRequest(val tagId: String)

    @Serializable
    data class ReorderItemsRequest(val itemIds: List<String>)

    @Serializable
    data class AddItemsResponse(val message: String, val addedIds: List<String>)

    @Serializable
    data class VendorIdCheckResponse(
        val available: Boolean,
        val conflictSectionName: String? = null,
        val conflictDeletedAt: String? = null
    )

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                prettyPrint = true
                isLenient = true
                ignoreUnknownKeys = true
            })
        }
        expectSuccess = false
    }

    /**
     * Handle 401 responses by clearing tokens and redirecting to login
     */
    private fun handleSessionExpiration() {
        val hadToken = !window.localStorage.getItem("id_token").isNullOrBlank()

        if (hadToken) {
            window.localStorage.removeItem("id_token")
            window.localStorage.removeItem("user_role")
            window.sessionStorage.setItem("session_expired", "true")
            console.warn("Session expired (401). Token cleared. Please log in again.")
            window.location.reload()
        }

        throw Exception("Session expired")
    }

    /**
     * Extension to check response status and handle 401
     */
    private suspend inline fun <reified T> HttpResponse.bodyOrHandle401(): T {
        if (status.value == 401) {
            handleSessionExpiration()
        }
        if (!status.isSuccess()) {
            val errorBody = try {
                body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Request failed with status ${status.value}")
            }
            throw Exception(errorBody.error)
        }
        return body()
    }

    private fun HttpRequestBuilder.withAuth() {
        val token = window.localStorage.getItem("id_token")
        if (!token.isNullOrBlank()) {
            header("Authorization", "Bearer $token")
        }
    }

    /**
     * Get all sections (optionally including deleted)
     *
     * @param includeDeleted Include soft-deleted sections
     * @return List of admin section DTOs
     */
    suspend fun getAllSections(includeDeleted: Boolean = false): List<AdminSectionDto> {
        val response = client.get("$baseUrl/api/admin/sections") {
            withAuth()
            parameter("includeDeleted", includeDeleted)
        }
        val sectionsResponse: SectionsResponse = response.bodyOrHandle401()
        return sectionsResponse.sections
    }

    /**
     * Get only archived (soft-deleted) sections
     *
     * @return List of archived section DTOs
     */
    suspend fun getArchivedSections(): List<AdminSectionDto> {
        val response = client.get("$baseUrl/api/admin/sections/archived") {
            withAuth()
        }
        val sectionsResponse: SectionsResponse = response.bodyOrHandle401()
        return sectionsResponse.sections
    }

    /**
     * Create new section
     *
     * @param request Section creation request
     * @return Created section DTO
     */
    suspend fun createSection(request: CreateSectionRequest): AdminSectionDto {
        val response = client.post("$baseUrl/api/admin/sections") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.bodyOrHandle401()
    }

    /**
     * Update existing section
     *
     * @param sectionId Section ID
     * @param request Update request
     * @return Updated section DTO
     */
    suspend fun updateSection(sectionId: String, request: UpdateSectionRequest): AdminSectionDto {
        val response = client.put("$baseUrl/api/admin/sections/$sectionId") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.bodyOrHandle401()
    }

    /**
     * Soft delete section
     *
     * @param sectionId Section ID
     */
    suspend fun deleteSection(sectionId: String) {
        val response = client.delete("$baseUrl/api/admin/sections/$sectionId") {
            withAuth()
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Delete failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }

    /**
     * Restore soft-deleted section
     *
     * @param sectionId Section ID
     */
    suspend fun restoreSection(sectionId: String) {
        val response = client.post("$baseUrl/api/admin/sections/$sectionId/restore") {
            withAuth()
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Restore failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }

    /**
     * Batch reorder sections
     *
     * @param sectionIds List of section IDs in desired order
     */
    suspend fun reorderSections(sectionIds: List<String>) {
        val response = client.put("$baseUrl/api/admin/sections/reorder") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(ReorderSectionsRequest(sectionIds))
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Reorder failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }

    /**
     * Get all items in a carousel section
     *
     * @param sectionId Section ID
     * @return List of content items
     */
    suspend fun getSectionItems(sectionId: String): List<com.castly.admin.dto.ContentResponse> {
        val response = client.get("$baseUrl/api/admin/sections/$sectionId/items") {
            withAuth()
        }
        val result: Map<String, List<com.castly.admin.dto.ContentResponse>> = response.bodyOrHandle401()
        return result["items"] ?: emptyList()
    }

    /**
     * Add items to carousel section via search
     *
     * @param sectionId Section ID
     * @param query Search query
     * @param contentType Content type (podcast/audiobook)
     * @param limit Maximum number of items to add
     * @return List of added item IDs
     */
    suspend fun addItemsBySearch(
        sectionId: String,
        query: String,
        contentType: String,
        limit: Int = 10
    ): List<String> {
        val response = client.post("$baseUrl/api/admin/sections/$sectionId/items/search") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(SearchItemsRequest(query, contentType, limit))
        }
        val result: AddItemsResponse = response.bodyOrHandle401()
        return result.addedIds
    }

    /**
     * Add single item to carousel section by ID
     *
     * Content type is determined automatically by the server from the section.
     *
     * @param sectionId Section ID
     * @param itemId Item UUID
     */
    suspend fun addItemById(
        sectionId: String,
        itemId: String
    ) {
        val response = client.post("$baseUrl/api/admin/sections/$sectionId/items") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(AddItemRequest(itemId))
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Add item failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }

    /**
     * Add items to carousel section by tag (static snapshot)
     *
     * @param sectionId Section ID
     * @param tagId Tag UUID
     * @return List of added item IDs
     */
    suspend fun addItemsByTag(
        sectionId: String,
        tagId: String
    ): List<String> {
        val response = client.post("$baseUrl/api/admin/sections/$sectionId/items/by-tag") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(AddItemsByTagRequest(tagId))
        }
        val result: AddItemsResponse = response.bodyOrHandle401()
        return result.addedIds
    }

    /**
     * Check if a vendor_id is available (not already in use by any section, including deleted)
     *
     * @param vendorId Vendor ID to check
     * @param excludeId Optional section ID to exclude (for updates - don't conflict with self)
     * @return VendorIdCheckResponse with availability status and conflict details
     */
    suspend fun checkVendorId(vendorId: String, excludeId: String? = null): VendorIdCheckResponse {
        val response = client.get("$baseUrl/api/admin/sections/vendor-id/check") {
            withAuth()
            parameter("vendorId", vendorId)
            if (excludeId != null) {
                parameter("excludeId", excludeId)
            }
        }
        return response.bodyOrHandle401()
    }

    /**
     * Remove item from carousel section
     *
     * @param sectionId Section ID
     * @param itemId Item UUID
     */
    suspend fun removeItem(
        sectionId: String,
        itemId: String
    ) {
        val response = client.delete("$baseUrl/api/admin/sections/$sectionId/items/$itemId") {
            withAuth()
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Remove item failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }

    /**
     * Reorder items within a carousel section
     *
     * @param sectionId Section ID
     * @param itemIds Ordered list of section_item UUIDs in desired display order
     */
    suspend fun reorderItems(
        sectionId: String,
        itemIds: List<String>
    ) {
        val response = client.put("$baseUrl/api/admin/sections/$sectionId/items/reorder") {
            withAuth()
            contentType(ContentType.Application.Json)
            setBody(ReorderItemsRequest(itemIds))
        }
        if (response.status.value == 401) {
            handleSessionExpiration()
        }
        if (!response.status.isSuccess()) {
            val errorBody = try {
                response.body<ErrorResponse>()
            } catch (e: Exception) {
                ErrorResponse("Reorder items failed with status ${response.status.value}")
            }
            throw Exception(errorBody.error)
        }
    }
}
